// Scout fáze — report kandidáta: 8 oblastí, AI předvyplnění, verdikt.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Robot, FloppyDisk, CaretDown, CaretUp } from "@phosphor-icons/react";
import { getScoutReport, putScoutReport, aiScoutReport } from "../../lib/lab-api";

export const SCOUT_AREA_LABELS = {
    minutes: "Jistota minut", role: "Role v týmu", fixtures: "Fixtures",
    form: "Forma a výkon", opponent_fit: "Fit na soupeře", value: "Hodnota",
    upside: "Upside", risk: "Bezrizikovost",
};
const VERDICTS = [
    { key: "ADVANCE_TO_FINALISTS", label: "→ Finalista", cls: "border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black" },
    { key: "KEEP_SCOUTING", label: "Dál scoutovat", cls: "border-fa-sky/50 text-fa-sky hover:bg-fa-sky hover:text-black" },
    { key: "MONITOR", label: "Jen sledovat", cls: "border-white/25 text-white/60 hover:text-white" },
    { key: "REJECT_FOR_FOCUS", label: "Vyřadit", cls: "border-red-400/40 text-red-300 hover:bg-red-400/20" },
];

export default function ScoutReportCard({ focus, candidate, onChanged }) {
    const [open, setOpen] = useState(false);
    const [report, setReport] = useState(null);
    const [ratings, setRatings] = useState({});
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        getScoutReport(focus.id, candidate.id).then((d) => {
            setReport(d.report);
            setRatings(d.report?.ratings || {});
            setNote(d.report?.manager_note || "");
        }).catch(() => {});
    }, [open, focus.id, candidate.id]);

    const runAi = async () => {
        setBusy(true);
        try {
            const d = await aiScoutReport(focus.id, candidate.id);
            setReport(d.report); setRatings(d.report?.ratings || {});
            toast.success("AI report vygenerován — uprav a dej verdikt");
        } catch (e) { toast.error(e.response?.data?.detail || "AI selhalo"); }
        finally { setBusy(false); }
    };

    const save = async (verdict = null) => {
        setBusy(true);
        try {
            const d = await putScoutReport(focus.id, candidate.id, {
                ratings, manager_note: note, ...(verdict ? { verdict } : {}),
            });
            setReport(d.report);
            toast.success(verdict ? "Verdikt uložen" : "Report uložen");
            if (verdict) { setOpen(false); onChanged?.(); }
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
        finally { setBusy(false); }
    };

    const avg = Object.keys(ratings).length
        ? Math.round(Object.values(ratings).reduce((a, b) => a + b, 0) / Object.keys(ratings).length)
        : null;

    return (
        <div className="border border-white/15 bg-[#0d0d0d]" data-testid={`scout-card-${candidate.fpl_id}`}>
            <button type="button" onClick={() => setOpen(!open)} data-testid={`scout-toggle-${candidate.fpl_id}`}
                    className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-white/[0.03]">
                <span className="font-display font-bold text-sm">{candidate.name}</span>
                <span className="font-mono text-[9px] text-white/45">{candidate.team} · {candidate.position} · £{candidate.price}</span>
                {candidate.is_my_tip && <span className="font-mono text-[8px] px-1.5 py-0.5 border border-fa-yellow/60 text-fa-yellow uppercase">Můj tip</span>}
                {avg != null && <span className="font-mono text-[10px] text-fa-mint font-bold tabular-nums ml-auto">Ø {avg}</span>}
                {avg == null && <span className="font-mono text-[9px] text-white/35 ml-auto">bez reportu</span>}
                {open ? <CaretUp size={12} className="text-white/40" /> : <CaretDown size={12} className="text-white/40" />}
            </button>
            {open && (
                <div className="px-3 pb-3 space-y-3 border-t border-white/10 pt-3">
                    <div className="flex gap-2 flex-wrap">
                        <button type="button" onClick={runAi} disabled={busy} data-testid={`scout-ai-${candidate.fpl_id}`}
                                className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-fa-violet text-fa-cream hover:bg-fa-violet flex items-center gap-1.5 disabled:opacity-40">
                            <Robot size={11} /> {busy ? "Pracuji…" : "AI předvyplnit"}
                        </button>
                        <button type="button" onClick={() => save()} disabled={busy} data-testid={`scout-save-${candidate.fpl_id}`}
                                className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-white/25 text-white/70 hover:text-white flex items-center gap-1.5 disabled:opacity-40">
                            <FloppyDisk size={11} /> Uložit
                        </button>
                    </div>
                    {report?.ai_summary && (
                        <p className="font-mono text-[10px] text-white/70 border border-fa-violet/30 bg-fa-violet/5 p-2" data-testid={`scout-summary-${candidate.fpl_id}`}>
                            {report.ai_summary}
                            {report.suggested_verdict && <span className="text-fa-cream"> — AI navrhuje: {VERDICTS.find((v) => v.key === report.suggested_verdict)?.label}</span>}
                        </p>
                    )}
                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
                        {Object.entries(SCOUT_AREA_LABELS).map(([k, label]) => (
                            <div key={k} className="flex items-center gap-2">
                                <span className="font-mono text-[9px] text-white/60 w-24">{label}</span>
                                <input type="range" min="0" max="100" value={ratings[k] ?? 50}
                                       onChange={(e) => setRatings({ ...ratings, [k]: Number(e.target.value) })}
                                       className="flex-1 accent-[#9EE8B4] h-1" data-testid={`scout-slider-${k}-${candidate.fpl_id}`} />
                                <span className="font-mono text-[10px] tabular-nums w-7 text-right text-white/80">{Math.round(ratings[k] ?? 50)}</span>
                            </div>
                        ))}
                    </div>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                              placeholder="Tvoje poznámka ke kandidátovi…"
                              className="w-full bg-black/40 border border-white/25 px-2 py-1.5 font-mono text-[11px] text-white focus:outline-none focus:border-fa-mint placeholder:text-white/35"
                              data-testid={`scout-note-${candidate.fpl_id}`} />
                    <div className="flex gap-1.5 flex-wrap">
                        <span className="font-mono text-[8px] uppercase tracking-widest text-white/40 self-center">Verdikt:</span>
                        {VERDICTS.map((v) => (
                            <button key={v.key} type="button" onClick={() => save(v.key)} disabled={busy}
                                    data-testid={`scout-verdict-${v.key}-${candidate.fpl_id}`}
                                    className={`font-mono text-[9px] uppercase px-2.5 py-1.5 border ${v.cls} disabled:opacity-40`}>
                                {v.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
